// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import { format, startOfDay } from 'date-fns'
import React, { useMemo } from 'react'
import { useStore, useTranslate } from 'react-admin'
import { LazyPlot as Plot } from '../common/LazyPlot'
import { useDeviceMetrics } from '../hooks/useDeviceMetrics'
import { GLUCOSE_THRESHOLDS, TIR_ZONE_COLORS } from '../types/dashboard'
import { formatGlucoseValue, type GlucoseUnit } from '../utils/glucoseUnits'
import { useDateFormatter } from '../utils/localeDate'
import { SHARED_METRICS_PER_PAGE } from '../utils/paginationUtils'
import { calculateTIRStats } from '../utils/tirStats'

export interface DailyTIRBarChartProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  onDayClick?: (payload: { date: Date; dateKey: string; shiftKey: boolean }) => void
  selectedDateKeys?: string[]
  glucoseUnit?: string // 'mmol/L' or 'mg/dL' - for PDF export unit conversion
  targetMin?: number
  targetMax?: number
  highMax?: number
}

interface DailyTIRData {
  date: Date
  dateKey: string
  veryLow: number // <3.0 mmol/L
  low: number // 3.0-3.8 mmol/L
  target: number // 3.9-10.0 mmol/L
  high: number // 10.1-13.9 mmol/L
  veryHigh: number // >13.9 mmol/L
  totalReadings: number
}

export const DailyTIRBarChart: React.FC<DailyTIRBarChartProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    onDayClick,
    selectedDateKeys = [],
    glucoseUnit: propGlucoseUnit,
    targetMin,
    targetMax,
    highMax,
  }) => {
    const [storeDeviceId] = useStore('selectedDeviceId')
    const [storePetId] = useStore('selectedPetId')
    const [storeGlucoseUnit] = useStore('glucoseUnit', 'mmol/L')
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId
    const glucoseUnit = propGlucoseUnit ?? storeGlucoseUnit
    const translate = useTranslate()
    const fmtDate = useDateFormatter()

    // Use the unified metrics hook that handles both single and virtual devices
    const {
      data: metricsData = [],
      isLoading,
      error,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'ASC' },
      pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
    })

    // Calculate daily TIR statistics via the canonical pipeline.
    // Group raw readings by calendar day (numeric timestamps only — string
    // timestamps would bypass BGM correction in calculateTIRStats), then run
    // each day's readings through calculateTIRStats so the per-day zone
    // buckets are BGM-corrected and exclude invalid readings (S2b family).
    const dailyStats = useMemo(() => {
      const dayBuckets = new Map<string, { date: Date; metrics: typeof metricsData }>()

      metricsData.forEach((metric) => {
        if (typeof metric.timestamp !== 'number' || !Number.isFinite(metric.timestamp)) {
          return
        }
        const dayStart = startOfDay(new Date(metric.timestamp * 1000))
        const dateKey = format(dayStart, 'yyyy-MM-dd')
        if (!dayBuckets.has(dateKey)) {
          dayBuckets.set(dateKey, { date: dayStart, metrics: [] })
        }
        dayBuckets.get(dateKey)!.metrics.push(metric)
      })

      const result: DailyTIRData[] = []
      dayBuckets.forEach(({ date, metrics }, dateKey) => {
        const stats = calculateTIRStats(
          metrics,
          undefined,
          undefined,
          targetMin,
          targetMax,
          highMax,
        )
        if (!stats || stats.total === 0) {
          return
        }
        result.push({
          date,
          dateKey,
          veryLow: stats.veryLowCount,
          low: stats.lowCount,
          target: stats.inRangeCount,
          high: stats.highCount,
          veryHigh: stats.veryHighCount,
          totalReadings: stats.total,
        })
      })

      // Sort by date
      return result.sort((a, b) => a.date.getTime() - b.date.getTime())
    }, [metricsData, targetMin, targetMax, highMax])

    if (isLoading) {
      return <div>{translate('pos.glucose.loading_daily_tir')}</div>
    }

    if (error) {
      console.error('DailyTIRBarChart error:', error)
      return (
        <div>
          {translate('pos.glucose.error_loading_daily_tir')}:{' '}
          {error?.message || translate('pos.common.unknown_error')}
        </div>
      )
    }

    if (dailyStats.length === 0) {
      return null // Don't show anything if no data
    }

    // Calculate percentages for each zone
    const dates = dailyStats.map((d) => fmtDate(d.date, 'dayMonth'))
    const selectedDateSet = new Set(selectedDateKeys)
    const hasSelection = selectedDateSet.size > 0
    const dayOpacities = dailyStats.map((d) =>
      hasSelection && !selectedDateSet.has(d.dateKey) ? 0.55 : 1,
    )
    const dateByKey = new Map(dailyStats.map((d) => [d.dateKey, d.date]))

    // Helper function to format time duration
    const formatDuration = (count: number) => {
      const minutes = count * 3 // 3-minute intervals for CGM data
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
    }

    // Unit may be a PROP override (PDF capture), so use the pure helper
    // with the resolved unit rather than useGlucoseDisplay.
    const formatThreshold = (mmolValue: number): string =>
      formatGlucoseValue(mmolValue, glucoseUnit as GlucoseUnit)

    // Format thresholds for zone labels (species-aware)
    const effMin = targetMin ?? GLUCOSE_THRESHOLDS.LOW
    const effMax = targetMax ?? GLUCOSE_THRESHOLDS.TARGET_MAX
    const veryLow = formatThreshold(GLUCOSE_THRESHOLDS.VERY_LOW)
    const low = formatThreshold(effMin)
    const high = formatThreshold(effMax)
    const highStart = formatThreshold(effMax + 0.1)
    const veryHigh = formatThreshold(highMax ?? GLUCOSE_THRESHOLDS.HIGH)

    // Prepare data for stacked bar chart (site-wide standard colors)
    // Stack order top to bottom: Very High → High → Target → Low → Very Low
    const zones = [
      {
        name: `${translate('pos.glucose.very_high')} (>${veryHigh})`,
        color: TIR_ZONE_COLORS.veryHigh,
        data: dailyStats.map((d) =>
          d.totalReadings > 0 ? (d.veryHigh / d.totalReadings) * 100 : 0,
        ),
        counts: dailyStats.map((d) => d.veryHigh),
      },
      {
        name: `${translate('pos.glucose.high')} (${highStart}–${veryHigh})`,
        color: TIR_ZONE_COLORS.high,
        data: dailyStats.map((d) => (d.totalReadings > 0 ? (d.high / d.totalReadings) * 100 : 0)),
        counts: dailyStats.map((d) => d.high),
      },
      {
        name: `${translate('pos.glucose.target')} (${low}–${high})`,
        color: TIR_ZONE_COLORS.target,
        data: dailyStats.map((d) => (d.totalReadings > 0 ? (d.target / d.totalReadings) * 100 : 0)),
        counts: dailyStats.map((d) => d.target),
      },
      {
        name: `${translate('pos.glucose.low')} (${veryLow}–${formatThreshold(effMin - 0.1)})`,
        color: TIR_ZONE_COLORS.low,
        data: dailyStats.map((d) => (d.totalReadings > 0 ? (d.low / d.totalReadings) * 100 : 0)),
        counts: dailyStats.map((d) => d.low),
      },
      {
        name: `${translate('pos.glucose.very_low')} (<${veryLow})`,
        color: TIR_ZONE_COLORS.veryLow,
        data: dailyStats.map((d) =>
          d.totalReadings > 0 ? (d.veryLow / d.totalReadings) * 100 : 0,
        ),
        counts: dailyStats.map((d) => d.veryLow),
      },
    ]

    // Create traces for Plotly
    // IMPORTANT: Plotly stacks bottom-to-top (first trace = bottom)
    // We want visual top-to-bottom: Very High → High → Target → Low → Very Low
    // So reverse zones array: Very Low (bottom) → ... → Very High (top)
    const traces = zones
      .slice()
      .reverse()
      .map((zone) => ({
        x: dates,
        y: zone.data,
        customdata: dailyStats.map((d) => d.dateKey),
        name: zone.name,
        type: 'bar' as const,
        marker: {
          color: zone.color,
          opacity: dayOpacities,
        },
        hovertemplate: zone.counts.map((count, idx) => {
          const percent = zone.data[idx]
          const duration = formatDuration(count)
          return `<b>${zone.name}</b><br>${percent.toFixed(1)}% (${duration})<extra></extra>`
        }),
      }))

    return (
      // Explicit wrapper height (matching layout.height) keeps Plotly's
      // useResizeHandler from measuring a collapsed parent on the first paint
      // and locking in a near-zero height until refresh (defect signature S6).
      <div
        data-testid="daily-tir-bar-chart"
        style={{ marginTop: '20px', marginBottom: '20px', height: 200 }}
      >
        <Plot
          key={`daily-tir-${deviceId}-${glucoseUnit}`}
          data={traces}
          layout={{
            title: '',
            barmode: 'stack',
            xaxis: {
              title: translate('resources.glucose.axis_labels.date'),
              tickangle: -45,
              tickfont: { size: 10 },
              gridcolor: 'transparent',
              fixedrange: true,
            },
            yaxis: {
              title: translate('pos.glucose.time_in_range_percent'),
              range: [0, 100],
              gridcolor: '#e0e0e0',
              ticksuffix: '%',
              fixedrange: true,
            },
            margin: { t: 10, l: 50, r: 10, b: 80 },
            height: 200,
            showlegend: true,
            legend: {
              orientation: 'h',
              x: 0,
              y: -0.3,
              xanchor: 'left',
              yanchor: 'top',
              font: { size: 10 },
            },
            plot_bgcolor: 'rgba(0,0,0,0)',
            paper_bgcolor: 'rgba(0,0,0,0)',
            font: {
              family: 'Roboto, sans-serif',
              size: 12,
              color: 'rgba(0, 0, 0, 0.87)',
            },
            hovermode: 'closest',
            hoverlabel: {
              bgcolor: '#000000',
              bordercolor: '#000000',
              font: { size: 12, color: '#FFFFFF', family: 'system-ui, -apple-system, sans-serif' },
            },
            dragmode: false,
          }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
          config={{
            displayModeBar: false,
            responsive: true,
            scrollZoom: false,
            doubleClick: false,
          }}
          onClick={(data) => {
            // Click-based selection callback for day-level filtering/linking
            if (data.points && data.points.length > 0 && onDayClick) {
              const point = data.points[0] as {
                pointIndex?: number
                pointNumber?: number
                customdata?: string
              }
              const rawPointIndex = point.pointIndex ?? point.pointNumber
              const pointIndex =
                typeof rawPointIndex === 'number' && Number.isInteger(rawPointIndex)
                  ? rawPointIndex
                  : null

              const clickedDateKey =
                (typeof point.customdata === 'string' ? point.customdata : null) ??
                (pointIndex !== null ? dailyStats[pointIndex]?.dateKey : null)
              const clickedDate =
                (clickedDateKey ? dateByKey.get(clickedDateKey) : null) ??
                (pointIndex !== null ? dailyStats[pointIndex]?.date : null)

              if (clickedDate && clickedDateKey) {
                onDayClick({
                  date: clickedDate,
                  dateKey: clickedDateKey,
                  shiftKey: !!data.event?.shiftKey,
                })
              }
            }
          }}
        />
      </div>
    )
  },
)
